import 'react-datepicker/dist/react-datepicker.css'

import IonIcon from '@reacticons/ionicons'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import DatePicker from 'react-datepicker'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { FilterOptionItem, TaxonomyType } from 'src/types'
import { getTaxonomyQueryFieldName } from 'src/utils/getTaxonomyQueryFieldName'

interface DateRangePickerProps {
  title: TaxonomyType
  items: FilterOptionItem[]
  opened: boolean
  disabled?: boolean
  showCheckbox?: boolean
  onSelectAccordion: (title: TaxonomyType) => void
  onClickWhenDisabled?: () => void
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  title,
  items,
  opened,
  disabled = false,
  onClickWhenDisabled,
  onSelectAccordion,
}) => {
  const [data, setData] = useState<FilterOptionItem[]>(items)

  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  const router = useRouter()
  const { updateQueryParams } = useUpdateQueryParams()
  const contentSpace = useRef(null)

  useEffect(() => {
    const startDataParam = getTaxonomyQueryFieldName(TaxonomyType.StartDate)

    if (!startDataParam) {
      setEndDate(null)

      const newData: any = [...data]
      newData.forEach((item: any, index: number) => {
        if (item?.name === 'To' && newData) {
          newData[index].value = null
        }
      })
      setData(newData)
    } else {
      const val = router.query[startDataParam]

      if (val && typeof val === 'string' && parseInt(val)) {
        setStartDate(new Date(parseInt(val)))
        const newData: any = [...data]
        newData.forEach((item: any, index: number) => {
          if (item?.name === 'From' && newData) {
            newData[index].value = new Date(parseInt(val))
          }
        })
        setData(newData)
        onSelectAccordion(title)
      } else {
        const newData: any = [...data]
        newData.forEach((item: any, index: number) => {
          if (item?.name === 'From' && newData) {
            newData[index].value = null
          }
        })
        setData(newData)
        setEndDate(null)
      }
    }
    const endDateParam = getTaxonomyQueryFieldName(TaxonomyType.EndDate)

    if (!endDateParam) {
      setEndDate(null)

      const newData: any = [...data]
      newData.forEach((item: any, index: number) => {
        if (item?.name === 'To' && newData) {
          newData[index].value = null
        }
      })
      setData(newData)
    } else {
      const val = router.query[endDateParam]

      if (val && typeof val === 'string' && parseInt(val)) {
        setEndDate(new Date(parseInt(val)))
        const newData: any = [...data]
        newData.forEach((item: any, index: number) => {
          if (item?.name === 'To' && newData) {
            newData[index].value = new Date(parseInt(val))
          }
        })
        setData(newData)
        onSelectAccordion(title)
      } else {
        const newData: any = [...data]
        newData.forEach((item: any, index: number) => {
          if (item?.name === 'To' && newData) {
            newData[index].value = null
          }
        })
        setData(newData)
        setEndDate(null)
      }
    }
  }, [router])

  const toggleAccordion = () => {
    if (disabled) {
      onClickWhenDisabled && onClickWhenDisabled()
      return
    }
    onSelectAccordion(title)
  }

  const onStartDateChanged = (value: Date | null) => {
    if (disabled) {
      onClickWhenDisabled && onClickWhenDisabled()
      return
    }
    let newStartDate = value
    if (endDate && newStartDate && newStartDate > endDate) {
      newStartDate = endDate
    }

    const newData: any = [...data]
    newData.forEach((item: any, index: number) => {
      if (item?.name === 'From' && newData) {
        newData[index].value = newStartDate
      }
    })

    setData(newData)
    setStartDate(newStartDate)
    onSelectAccordion(title)

    if (newStartDate) {
      updateQueryParams({ startDate: newStartDate.getTime().toString() })
    } else {
      updateQueryParams({ startDate: undefined })
    }
  }

  const onEndDateChanged = (value: Date | null) => {
    if (disabled) {
      onClickWhenDisabled && onClickWhenDisabled()
      return
    }
    let newEndDate = value
    if (startDate && newEndDate && newEndDate < startDate) {
      newEndDate = startDate
    }
    const newData: any = [...data]
    newData.forEach((item: any, index: number) => {
      if (item?.name === 'To' && newData) {
        newData[index].value = newEndDate
      }
    })

    setData(newData)
    setEndDate(newEndDate)
    onSelectAccordion(title)

    if (newEndDate) {
      updateQueryParams({ endDate: newEndDate.getTime().toString() })
    } else {
      updateQueryParams({ endDate: undefined })
    }
  }

  return (
    <div className="w-full flex flex-col">
      <button
        className="py-3 box-border appearance-none cursor-pointer focus:outline-none flex items-center justify-between"
        onClick={toggleAccordion}
      >
        <p className="inline-block text-footnote light  font-bold font-primary text-textdark uppercase text-[0.94rem]">
          {title}
        </p>
        {opened ? <IonIcon name="chevron-up-outline" /> : <IonIcon name="chevron-down-outline" />}
      </button>
      <div
        ref={contentSpace}
        style={{ maxHeight: opened ? `380px` : '0px' }}
        className="pl-[20px] transition-max-height duration-500 overflow-hidden overflow-y-scroll ease-in-out items-center"
      >
        {/* <div className="rounded mr-[5px] w-5 h-5 flex-shrink-0" onClick={toggleCheck}>
          {selected && <IonIcon name="checkbox" className="text-navy" />}
          {!selected && <IonIcon name="square-outline" className="text-textdark opacity-40" />}
        </div> */}
        <DatePicker
          disabled={disabled}
          placeholderText={'From'}
          selected={data[0]?.value}
          onChange={onStartDateChanged}
        />
        <DatePicker
          placeholderText={'To'}
          disabled={disabled}
          selected={data[1]?.value}
          onChange={onEndDateChanged}
          className="mt-[10px]"
        />
      </div>
    </div>
  )
}
